import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import {
  CONTRACT_STATUSES,
  type ContractStatus,
  type ContractSummary,
} from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { StatusBadge } from '../components/StatusBadge';
import { ageFrom, formatDate } from '../lib/format';

export function DashboardPage() {
  const { fetcher } = useAuth();
  const all = useQuery({
    queryKey: ['contracts', 'all'],
    queryFn: () =>
      fetcher<{ contracts: ContractSummary[] }>('/api/contracts'),
  });

  const counts: Record<ContractStatus, number> = {
    draft: 0,
    sent: 0,
    viewed: 0,
    signed: 0,
    declined: 0,
    void: 0,
    completed: 0,
  };
  const contracts = all.data?.contracts ?? [];
  for (const c of contracts) {
    if (c.status in counts) counts[c.status]++;
  }
  const recent = contracts.slice(0, 10);
  const pending = contracts
    .filter((c) => c.status === 'sent' || c.status === 'viewed')
    .slice(0, 10);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Dashboard</h1>
      {all.isError ? (
        <p className="text-sm text-red-600">Failed to load contracts.</p>
      ) : null}
      <section>
        <h2 className="mb-2 text-sm font-medium uppercase text-slate-500">
          Status
        </h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
          {CONTRACT_STATUSES.map((s) => (
            <div key={s} className="card">
              <div className="text-xs uppercase text-slate-500">{s}</div>
              <div className="mt-1 text-2xl font-semibold">{counts[s]}</div>
            </div>
          ))}
        </div>
      </section>
      <section>
        <h2 className="mb-2 text-sm font-medium uppercase text-slate-500">
          Pending signatures
        </h2>
        <div className="card overflow-x-auto">
          {pending.length === 0 ? (
            <p className="text-sm text-slate-500">Nothing pending.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-2 py-1">Contract</th>
                  <th className="px-2 py-1">Status</th>
                  <th className="px-2 py-1">Sent</th>
                  <th className="px-2 py-1">Age</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((c) => (
                  <tr key={c.id} className="border-t border-slate-100">
                    <td className="px-2 py-1">
                      <Link
                        to={`/contracts/${c.id}`}
                        className="text-brand hover:underline"
                      >
                        {c.id.slice(0, 8)}
                      </Link>
                    </td>
                    <td className="px-2 py-1">
                      <StatusBadge status={c.status} />
                    </td>
                    <td className="px-2 py-1 text-slate-600">
                      {formatDate(c.sent_at)}
                    </td>
                    <td className="px-2 py-1 text-slate-600">
                      {ageFrom(c.sent_at ?? c.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
      <section>
        <h2 className="mb-2 text-sm font-medium uppercase text-slate-500">
          Recent activity
        </h2>
        <div className="card overflow-x-auto">
          {recent.length === 0 ? (
            <p className="text-sm text-slate-500">No contracts yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-2 py-1">Contract</th>
                  <th className="px-2 py-1">Status</th>
                  <th className="px-2 py-1">Created</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((c) => (
                  <tr key={c.id} className="border-t border-slate-100">
                    <td className="px-2 py-1">
                      <Link
                        to={`/contracts/${c.id}`}
                        className="text-brand hover:underline"
                      >
                        {c.id.slice(0, 8)}
                      </Link>
                    </td>
                    <td className="px-2 py-1">
                      <StatusBadge status={c.status} />
                    </td>
                    <td className="px-2 py-1 text-slate-600">
                      {formatDate(c.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}
